import { createSseParser, type SseEvent } from "./stream.js";
import type {
  ChatConfig,
  ChatHistoryItem,
  ChatHistoryResponse,
  ChatHistoryResult,
  ChatResumeResponse,
  ChatShareResponse,
} from "./types.js";

export type StreamHandlers = {
  onEvent(event: SseEvent): void;
};

export type JonasSearchResultDto = {
  title: string;
  url: string;
  description: string;
  type: string;
  relevancy: number;
};

export class ChatResumeError extends Error {
  readonly status: number;

  readonly code: string;

  readonly retryable: boolean | undefined;

  constructor(
    message: string,
    options: {
      status: number;
      code?: string;
      retryable?: boolean | undefined;
    },
  ) {
    super(message);
    this.name = "ChatResumeError";
    this.status = options.status;
    this.code = options.code ?? "";
    this.retryable = options.retryable;
  }
}

export class ChatStreamError extends Error {
  readonly status: number;

  readonly rateLimit: boolean;

  constructor(
    message: string,
    options: { status: number; rateLimit?: boolean },
  ) {
    super(message);
    this.name = "ChatStreamError";
    this.status = options.status;
    this.rateLimit = options.rateLimit === true;
  }
}

export class ChatHistoryRemovalError extends Error {
  readonly status: number;

  readonly code: string;

  readonly retryable: boolean | undefined;

  constructor(
    message: string,
    options: {
      status: number;
      code?: string;
      retryable?: boolean | undefined;
    },
  ) {
    super(message);
    this.name = "ChatHistoryRemovalError";
    this.status = options.status;
    this.code = options.code ?? "";
    this.retryable = options.retryable;
  }
}

export class ChatApiClient {
  constructor(private readonly config: ChatConfig) {}

  // Shared with the resume and history-removal paths: one translated, duration-neutral sentence
  // rather than the server's Dutch-only body, which can also name a wait that Retry-After contradicts.
  private rateLimitMessage(): string {
    return (
      this.config.labels?.resumeRateLimited ||
      "Er worden te veel verzoeken verstuurd. Wacht even en probeer het opnieuw."
    );
  }

  async postStream(
    url: string,
    payload: unknown,
    handlers: StreamHandlers,
    signal: AbortSignal,
  ): Promise<void> {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        Accept: "text/event-stream",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
      signal,
    });

    if (!response.ok) {
      if (response.status === 429) {
        throw new ChatStreamError(this.rateLimitMessage(), {
          status: 429,
          rateLimit: true,
        });
      }
      throw new ChatStreamError(`Chat request failed with ${response.status}`, {
        status: response.status,
      });
    }
    if (!response.body) {
      throw new Error("Chat response did not include a stream body.");
    }

    const parser = createSseParser(handlers.onEvent);
    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    while (true) {
      const result = await reader.read();
      if (result.done) break;
      parser.push(decoder.decode(result.value, { stream: true }));
    }

    parser.push(decoder.decode());
    parser.flush();
  }

  start(
    payload: unknown,
    handlers: StreamHandlers,
    signal: AbortSignal,
  ): Promise<void> {
    return this.postStream(
      this.config.endpoints.start,
      payload,
      handlers,
      signal,
    );
  }

  continue(
    payload: unknown,
    handlers: StreamHandlers,
    signal: AbortSignal,
  ): Promise<void> {
    return this.postStream(
      this.config.endpoints.continue,
      payload,
      handlers,
      signal,
    );
  }

  async redeem(shareToken: string): Promise<ChatResumeResponse> {
    const response = await fetch(
      this.config.endpoints.redeem ?? "/api/chat/redeem",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ shareToken }),
      },
    );
    if (!response.ok) {
      const failure = await readJsonObject(response);
      throw new Error(
        stringValue(
          failure.message,
          `Redeem request failed with ${response.status}`,
        ),
      );
    }
    return (await response.json()) as ChatResumeResponse;
  }

  async cancel(sessionToken: string): Promise<void> {
    const endpoint = this.config.endpoints.cancel;
    if (!endpoint || !sessionToken) return;
    try {
      await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionToken }),
        keepalive: true,
      });
    } catch {
      // Best-effort: cancellation is advisory. The caller aborts the local stream
      // regardless, and a page refresh still recovers the persisted answer.
    }
  }

  async postFeedback(payload: unknown): Promise<void> {
    const response = await fetch(this.config.endpoints.feedback, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error(`Feedback request failed with ${response.status}`);
    }
  }

  async clearFeedback(payload: unknown): Promise<void> {
    // Retract a rating (thumbs back to neutral): same route as postFeedback, DELETE verb.
    const response = await fetch(this.config.endpoints.feedback, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error(`Feedback clear failed with ${response.status}`);
    }
  }

  async share(payload: {
    sessionToken: string;
    messageId: string;
  }): Promise<ChatShareResponse> {
    const response = await fetch(
      this.config.endpoints.share ?? "/api/chat/share",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      },
    );
    if (!response.ok) {
      const failure = await readJsonObject(response);
      throw new Error(
        stringValue(
          failure.message,
          `Share request failed with ${response.status}`,
        ),
      );
    }
    return (await response.json()) as ChatShareResponse;
  }

  async resume(sessionToken: string): Promise<ChatResumeResponse> {
    const response = await fetch(this.config.endpoints.resume, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sessionToken }),
    });
    if (!response.ok) {
      const failure = await readJsonObject(response);
      throw new ChatResumeError(
        stringValue(
          failure.message,
          `Resume request failed with ${response.status}`,
        ),
        {
          status: response.status,
          code: stringValue(failure.code),
          retryable:
            typeof failure.retryable === "boolean"
              ? failure.retryable
              : undefined,
        },
      );
    }
    return (await response.json()) as ChatResumeResponse;
  }

  async getHistory(scope: {
    product: string;
    languageId: number;
    activation: string;
    activeSessionToken?: string;
  }): Promise<ChatHistoryResult> {
    const params = new URLSearchParams({
      product: scope.product ?? "",
      languageId: String(scope.languageId ?? 1),
      activation: scope.activation ?? "",
    });
    if (scope.activeSessionToken) {
      params.set("activeSessionToken", scope.activeSessionToken);
    }
    const response = await fetch(
      `${this.config.endpoints.history}?${params.toString()}`,
      {
        headers: { Accept: "application/json" },
      },
    );
    if (!response.ok) {
      throw new Error(`History request failed with ${response.status}`);
    }
    const body = (await response.json()) as ChatHistoryResponse;
    return {
      items: Array.isArray(body?.items) ? body.items : [],
      keepHistory: body?.keepHistory === true,
    };
  }

  async setHistoryPreference(keepHistory: boolean): Promise<boolean> {
    const response = await fetch(this.config.endpoints.historyPreference, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ keepHistory }),
    });
    if (!response.ok) {
      throw new Error(`Preference request failed with ${response.status}`);
    }
    const body = (await response.json()) as { keepHistory?: boolean };
    return body?.keepHistory === true;
  }

  async startNewOffChat(): Promise<void> {
    const response = await fetch(this.config.endpoints.newChat, {
      method: "POST",
    });
    if (!response.ok) {
      throw new Error(`New chat request failed with ${response.status}`);
    }
  }

  async resetHistory(): Promise<void> {
    const response = await fetch(this.config.endpoints.history, {
      method: "DELETE",
    });
    if (!response.ok) {
      throw new Error(`Reset history request failed with ${response.status}`);
    }
  }

  async removeHistoryItem(sessionToken: string): Promise<void> {
    const response = await fetch(this.config.endpoints.historyItem, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sessionToken }),
    });
    if (!response.ok) {
      const failure = await readJsonObject(response);
      throw new ChatHistoryRemovalError(
        stringValue(
          failure.message,
          `Remove history request failed with ${response.status}`,
        ),
        {
          status: response.status,
          code: stringValue(failure.code),
          retryable:
            typeof failure.retryable === "boolean"
              ? failure.retryable
              : undefined,
        },
      );
    }
  }

  async getArticleSource(url: string): Promise<string> {
    const response = await fetch(
      `${this.config.endpoints.articleSource}?url=${encodeURIComponent(url)}`,
    );
    if (!response.ok) {
      throw new Error(`Article source request failed with ${response.status}`);
    }
    return await response.text();
  }

  async search(params: {
    query: string;
    product: string;
    languageId: number;
    area: string;
    numberOfSources: number;
    signal?: AbortSignal;
  }): Promise<JonasSearchResultDto[]> {
    const endpoint = this.config.endpoints.search;
    if (!endpoint) return [];
    const url = new URL(endpoint, window.location.origin);
    url.searchParams.set("query", params.query);
    if (params.product) url.searchParams.set("product", params.product);
    url.searchParams.set("languageId", String(params.languageId));
    if (params.area) url.searchParams.set("area", params.area);
    url.searchParams.set("numberOfSources", String(params.numberOfSources));
    const response = await fetch(url.toString(), {
      headers: { Accept: "application/json" },
      signal: params.signal ?? null,
    });
    if (!response.ok) {
      throw new Error(`Search request failed with ${response.status}`);
    }
    const data = (await response.json()) as unknown;
    return Array.isArray(data) ? (data as JonasSearchResultDto[]) : [];
  }
}

async function readJsonObject(
  response: Response,
): Promise<Record<string, unknown>> {
  try {
    const value = (await response.clone().json()) as unknown;
    return isRecord(value) ? value : {};
  } catch {
    return {};
  }
}

function stringValue(value: unknown, fallback = ""): string {
  return typeof value === "string" && value ? value : fallback;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
